import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Edit, Plus, Trash2, Power, Dumbbell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  id: '',
  title_ar: '',
  title_en: '',
  title_fr: '',
  sessions: 1,
  price_usd: 0,
  offer_price_usd: null,
  enabled: true,
  sort_order: 99,
};

export const AdminPhysioPackagesView = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/physio-packages`, { withCredentials: true });
      setPackages(res.data || []);
    } catch (e) {
      console.error('Failed to load physio packages:', e);
      toast.error(t('فشل تحميل الباقات', 'Failed to load packages'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...BLANK });
    setOpen(true);
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({ ...BLANK, ...pkg });
    setOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const cleaned = {
      ...form,
      sessions: parseInt(form.sessions, 10) || 1,
      price_usd: parseFloat(form.price_usd) || 0,
      offer_price_usd: form.offer_price_usd === '' || form.offer_price_usd == null
        ? null : parseFloat(form.offer_price_usd),
      sort_order: parseInt(form.sort_order, 10) || 99,
    };

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/physio-packages/${editingId}`, cleaned, { withCredentials: true });
        toast.success(t('تم التحديث', 'Updated'));
      } else {
        if (!cleaned.id) {
          toast.error(t('يجب إدخال معرف الباقة', 'Package id is required'));
          setSaving(false);
          return;
        }
        await axios.post(`${API}/admin/physio-packages`, cleaned, { withCredentials: true });
        toast.success(t('تمت الإضافة', 'Created'));
      }
      setOpen(false);
      await fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(t(
      `هل أنت متأكد من حذف الباقة "${pkg.title_ar || pkg.id}"؟`,
      `Delete package "${pkg.title_en || pkg.id}"?`
    ))) return;
    try {
      await axios.delete(`${API}/admin/physio-packages/${pkg.id}`, { withCredentials: true });
      toast.success(t('تم الحذف', 'Deleted'));
      await fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const toggleEnabled = async (pkg) => {
    try {
      await axios.put(
        `${API}/admin/physio-packages/${pkg.id}`,
        { enabled: !pkg.enabled },
        { withCredentials: true }
      );
      await fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-physio-packages-view" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-bold text-[#2c3e50] flex items-center gap-2">
          <Dumbbell size={20} className="text-[#1e88e5]" />
          {t('باقات العلاج الطبيعي', 'Physiotherapy Packages')}
        </h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e88e5] hover:bg-[#1976d2]" onClick={startCreate} data-testid="add-physio-package-button">
              <Plus size={18} className={isRTL ? 'ml-2' : 'mr-2'} />
              {t('إضافة باقة', 'Add Package')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingId
                  ? t('تعديل باقة العلاج الطبيعي', 'Edit Physio Package')
                  : t('إضافة باقة جديدة', 'Add New Package')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingId && (
                <div>
                  <Label htmlFor="ph-id">{t('معرف الباقة (إنجليزي، بدون مسافات)', 'Package ID (slug)')}</Label>
                  <Input
                    id="ph-id"
                    value={form.id}
                    onChange={(e) => setField('id', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    placeholder="e.g. sports_injury"
                    required
                    data-testid="physio-package-id-input"
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>{t('العنوان بالعربية', 'Arabic Title')}</Label>
                  <Input value={form.title_ar} onChange={(e) => setField('title_ar', e.target.value)} data-testid="physio-title-ar" />
                </div>
                <div>
                  <Label>{t('العنوان بالإنجليزية', 'English Title')}</Label>
                  <Input value={form.title_en} onChange={(e) => setField('title_en', e.target.value)} data-testid="physio-title-en" />
                </div>
                <div>
                  <Label>{t('العنوان بالفرنسية', 'French Title')}</Label>
                  <Input value={form.title_fr} onChange={(e) => setField('title_fr', e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label>{t('عدد الجلسات', 'Sessions')}</Label>
                  <Input type="number" min="1" value={form.sessions} onChange={(e) => setField('sessions', e.target.value)} required data-testid="physio-sessions" />
                </div>
                <div>
                  <Label>{t('السعر الأصلي ($)', 'Price ($)')}</Label>
                  <Input type="number" step="0.01" value={form.price_usd} onChange={(e) => setField('price_usd', e.target.value)} required data-testid="physio-price" />
                </div>
                <div>
                  <Label>{t('سعر العرض ($)', 'Offer Price ($)')}</Label>
                  <Input
                    type="number" step="0.01"
                    value={form.offer_price_usd ?? ''}
                    onChange={(e) => setField('offer_price_usd', e.target.value)}
                    placeholder="—"
                  />
                </div>
                <div>
                  <Label>{t('الترتيب', 'Sort Order')}</Label>
                  <Input type="number" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Switch checked={!!form.enabled} onCheckedChange={(v) => setField('enabled', v)} />
                <span className="text-sm">{t('مفعّل', 'Enabled')}</span>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t('إلغاء', 'Cancel')}</Button>
                <Button type="submit" disabled={saving} className="bg-[#1e88e5] hover:bg-[#1976d2]" data-testid="save-physio-package-btn">
                  {saving ? t('جارٍ الحفظ…', 'Saving…') : t('حفظ', 'Save')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-gray-500">{t('جاري التحميل…', 'Loading…')}</p>
      ) : packages.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">{t('لا توجد باقات', 'No packages')}</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`p-5 ${!pkg.enabled ? 'opacity-60' : ''}`}
              data-testid={`admin-physio-card-${pkg.id}`}
            >
              <div className="mb-2">
                <p className="text-xs text-gray-500 font-mono">{pkg.id}</p>
                <h3 className="font-bold text-[#2c3e50] leading-tight">
                  {language === 'ar' ? pkg.title_ar : pkg.title_en}
                </h3>
                {language !== 'ar' && pkg.title_ar && (
                  <p className="text-xs text-gray-500 mt-0.5">{pkg.title_ar}</p>
                )}
              </div>

              <div className="mb-3">
                {pkg.offer_price_usd && pkg.offer_price_usd < pkg.price_usd ? (
                  <>
                    <span className="text-sm text-gray-400 line-through me-2">${pkg.price_usd}</span>
                    <span className="text-2xl font-bold text-[#1e88e5]">${pkg.offer_price_usd}</span>
                  </>
                ) : (
                  <span className="text-2xl font-bold text-[#1e88e5]">${pkg.price_usd}</span>
                )}
                <span className="text-gray-500 text-sm"> · {pkg.sessions} {t('جلسات', 'sessions')}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(pkg)} data-testid={`edit-physio-${pkg.id}`}>
                  <Edit size={14} className={isRTL ? 'ml-1' : 'mr-1'} /> {t('تعديل', 'Edit')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleEnabled(pkg)} data-testid={`toggle-physio-${pkg.id}`}>
                  <Power size={14} className={isRTL ? 'ml-1' : 'mr-1'} />
                  {pkg.enabled ? t('تعطيل', 'Disable') : t('تفعيل', 'Enable')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete(pkg)}
                  data-testid={`delete-physio-${pkg.id}`}
                >
                  <Trash2 size={14} className={isRTL ? 'ml-1' : 'mr-1'} /> {t('حذف', 'Delete')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPhysioPackagesView;
